import { OptimisationTarget, PerformanceMode } from '../../../targets'
import { BiddingStrategy, BidFactory } from '../../../bidding-strategies/bidding_strategies'
import { common, enums, resources } from '../../../gads'
import { GadsFields, GadsFieldsRequired } from '../../../utils/global'
import { BiddingStrategies } from '../../..'

export interface ProductGroup {
    name: string
    product_group_attribute: string
    resource_name: string | null
    product_groups?: ProductGroup[]
    generated_product_group?: boolean
    cost: number
    clicks: number
    impressions: number
}

export interface OtherRelevantProductGroups {
    name: string
    metrics: ProductGroupMetrics
    attribute: keyof common.IListingDimensionInfo
    resource_name: string
    focus: boolean
}

export interface ProductGroupMetrics {
    clicks: number
    impressions: number
    cost_micros: number
    all_conversions: number
    all_conversions_value: number
    search_absolute_top_impression_share: number
    search_impression_share: number
}

export type OptimisationTargetWithValues = {
    [target in OptimisationTarget]?: number
}

export interface ProductGroupMetricsPerAdGroup {
    [ad_group_resource_name: string]: { metrics: ProductGroupMetrics }
}

export type ImprovementEntityLocation = {
    campaign_label: string
    campaign_resource_name: string
    ad_group_label: string
    ad_group_resource_name: string
    product_group_label: string
    product_group_resource_name: string
}

export namespace AdjustProductGroupBid {
    export interface Body extends ImprovementEntityLocation {
        campaign_group_label: string
        group_cpa: number
        group_roas: number
        conversion_actions: string[]
        ad_group_criterion_resource_name: string
        current_cpc_bid: number
        bid_change: number
        is_increase: boolean
        metrics: ProductGroupMetrics
        product_group_tree: ProductGroup[]
        attribute: keyof common.IListingDimensionInfo
        readable_attribute: string
        target?: OptimisationTargetWithValues // Not used anymore, but still required for completed/dismisssed improvements
        performance_target: number
        is_using_target: boolean
        bidding_strategy?: BiddingStrategy // Not used anymore, but still required for completed/dismisssed improvements
        predictions: BodyPredictions | null
        siblings?: OtherRelevantProductGroups[]
        sibling_resource_names?: string[]

        // New variables
        performance_mode: PerformanceMode
        is_using_bid_factory: boolean
        simulation_points?: BiddingStrategies.BidFactory.SimulationPoint[]
        prediction_items?: {
            x: number //  cpc_bid
            y: number // cpa | roas
        }[]
        new_bid: number
    }

    export type BodyPredictions = Pick<BidFactory.BestBid, 'best_cpc_bid'>

    export interface ExtraDetails {
        cpc_bid?: number
    }

    export const Error = {
        NO_CPC_BID_TO_UPDATE: 'bid_undefined',
    } as const

    export type Error = (typeof Error)[keyof typeof Error]

    export const ErrorReadable = {
        [Error.NO_CPC_BID_TO_UPDATE]: 'Tried to update a criterion without a bid',
    }
}

export namespace ResyncProductGroup {
    export interface Body extends ImprovementEntityLocation {
        // Improvement header
        product_groups_hierarchy: string[]
        new_product_groups_attribute: string
        is_manual_cpc: boolean

        // Improvement graphic
        current_product_group_tree: ProductGroup
        generated_product_group_tree: ProductGroup[]
        ad_group_criterion_resource_name: string
        unsynced_product_group_depth: number

        // Push Data
        new_product_groups: RawProductGroup[]

        // Lookback Window
        lookback_window: number
    }

    // eslint-disable-next-line @typescript-eslint/no-empty-interface
    export interface ExtraDetails {}

    export const Error = {
        NO_OUT_OF_SYNC_PRODUCT_GROUP: 'no_out_of_sync_product_groups',
        INVALID_CASE_VALUE: 'invalid_case_value',
        INVALID_PRODUCT_CHANNEL_EXCLUSIVITY_CASE_VALUE:
            'invalid_product_channel_exclusivity_case_value',
        INVALID_PRODUCT_CHANNEL_CASE_VALUE: 'invalid_product_channel_case_value',
        INVALID_PRODUCT_CUSTOM_ATTRIBUTE_CASE_VALUE: 'invalid_product_custom_attribute_case_value',
        INVALID_PRODUCT_CONDITION_CASE_VALUE: 'invalid_product_condition_case_value',
        INVALID_PRODUCT_TYPE_CASE_VALUE: 'invalid_product_type_case_value',
        INVALID_PRODUCT_CATEGORY_CASE_VALUE: 'invalid_product_category_case_value',
        NO_CASE_VALUE: 'no_case_value',
    } as const

    export type Error = (typeof Error)[keyof typeof Error]

    export const ErrorReadable = {
        [Error.NO_OUT_OF_SYNC_PRODUCT_GROUP]: 'No "Everything else" Product Groups detected',
        [Error.INVALID_PRODUCT_CHANNEL_EXCLUSIVITY_CASE_VALUE]:
            'The generated product channel exclusivity case value is malformed',
        [Error.INVALID_PRODUCT_CHANNEL_CASE_VALUE]:
            'The generated product channel case value is malformed',
        [Error.INVALID_PRODUCT_CUSTOM_ATTRIBUTE_CASE_VALUE]:
            'The generated custom attribute case value is malformed',
        [Error.INVALID_PRODUCT_CONDITION_CASE_VALUE]:
            'The generated product condition case value is malformed',
        [Error.INVALID_PRODUCT_TYPE_CASE_VALUE]:
            'The generated product type case value is malformed',
        [Error.INVALID_PRODUCT_CATEGORY_CASE_VALUE]:
            'The generated product category case value is malformed',
        [Error.NO_CASE_VALUE]: 'No case value was generated',
    }
}

export interface RawProductGroup {
    product_group_view: GadsFieldsRequired<resources.ProductGroupView, 'resource_name'>
    campaign?: GadsFields<
        resources.Campaign,
        'id' | 'resource_name' | 'name' | 'bidding_strategy_type' | 'advertising_channel_sub_type'
    >
    ad_group: GadsFields<resources.AdGroup, 'id' | 'resource_name' | 'name' | 'cpc_bid_micros'>
    ad_group_criterion: GadsFields<
        resources.AdGroupCriterion,
        'listing_group' | 'resource_name' | 'effective_cpc_bid_micros' | 'negative'
    >
    metrics: GadsFields<
        common.Metrics,
        | 'clicks'
        | 'impressions'
        | 'cost_micros'
        | 'all_conversions'
        | 'all_conversions_value'
        | 'search_absolute_top_impression_share'
        | 'search_impression_share'
    >
}

export interface GeneratedAdGroupCriteria {
    ad_group: string
    cpc_bid_micros: number
    listing_group: {
        parent_ad_group_criterion: string
        case_value: common.IListingDimensionInfo
        type: enums.ListingGroupType.UNIT
    }
}

export interface RawShoppingPerformanceView {
    ad_group: {
        resource_name: string
    }
    metrics: {
        clicks: number
        impressions: number
        cost_micros: number
    }
    segments: {
        [shopping_performance_segment in ShoppingPerformanceSegment]?: string
    }
}

export interface PredictionsGroupedByAdGroup {
    [ad_group_resource_name: string]: BidFactory.BestBid[]
}

export type ShoppingPerformanceSegment =
    | 'product_brand'
    | 'product_channel'
    | 'product_type_l1'
    | 'product_type_l2'
    | 'product_type_l3'
    | 'product_type_l4'
    | 'product_type_l5'
    | 'product_item_id'
    | 'product_store_id'
    | 'product_merchant_id'
    | 'product_custom_attribute0'
    | 'product_custom_attribute1'
    | 'product_custom_attribute2'
    | 'product_custom_attribute3'
    | 'product_custom_attribute4'
    | 'product_channel_exclusivity'
    | 'product_category_level1'
    | 'product_category_level2'
    | 'product_category_level3'
    | 'product_category_level4'
    | 'product_category_level5'

export type ProductCategoryLevel =
    | enums.ProductCategoryLevel.LEVEL1
    | enums.ProductCategoryLevel.LEVEL2
    | enums.ProductCategoryLevel.LEVEL3
    | enums.ProductCategoryLevel.LEVEL4
    | enums.ProductCategoryLevel.LEVEL5
    | enums.ProductCategoryLevel.UNKNOWN
    | enums.ProductCategoryLevel.UNSPECIFIED

export type ProductTypeLevel =
    | enums.ProductTypeLevel.LEVEL1
    | enums.ProductTypeLevel.LEVEL2
    | enums.ProductTypeLevel.LEVEL3
    | enums.ProductTypeLevel.LEVEL4
    | enums.ProductTypeLevel.LEVEL5
    | enums.ProductTypeLevel.UNKNOWN
    | enums.ProductTypeLevel.UNSPECIFIED

export type ProductCustomAttributeIndex =
    | enums.ProductCustomAttributeIndex.INDEX0
    | enums.ProductCustomAttributeIndex.INDEX1
    | enums.ProductCustomAttributeIndex.INDEX2
    | enums.ProductCustomAttributeIndex.INDEX3
    | enums.ProductCustomAttributeIndex.INDEX4
    | enums.ProductCustomAttributeIndex.UNKNOWN
    | enums.ProductCustomAttributeIndex.UNSPECIFIED
